import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, inject, OnInit, Output, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatPaginator, MatPaginatorIntl } from '@angular/material/paginator';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTable, MatTableDataSource, MatTableModule } from '@angular/material/table';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faRotateRight } from '@fortawesome/free-solid-svg-icons';
import { CustomerPropertyCodes } from '@hub4b/constantes/customer-property-codes';
import { SortDirections } from '@hub4b/constantes/sort-directions';
import { FoldersListCounterComponent } from '@hub4b/controls/datas-list/folders-list-counter/folders-list-counter.component';
import { ContextualActionType } from '@hub4b/controls/datas-list/table-action-cell/models/contextual-action-type';
import { ContextualMenuOpenEvent } from '@hub4b/controls/datas-list/table-action-cell/models/contextual-menu-open-event';
import { ContextualMenuSelectEvent } from '@hub4b/controls/datas-list/table-action-cell/models/contextual-menu-select-event';
import { TableActionCellComponent } from '@hub4b/controls/datas-list/table-action-cell/table-action-cell.component';
import { TableCellComponent } from '@hub4b/controls/datas-list/table-cell/table-cell.component';
import { TableHeaderCellComponent } from '@hub4b/controls/datas-list/table-header-cell/table-header-cell.component';
import { BaseDirective } from '@hub4b/directives/base.directive';
import { ArrayHelper } from '@hub4b/helpers/array-helper';
import { ColumnType } from '@hub4b/models/enums/column-type';
import { FilterType } from '@hub4b/models/enums/filter-type';
import { PropertyType } from '@hub4b/models/enums/property-type';
import { FolderCounterUpdatedEvent } from '@hub4b/models/events/folder-counter-updated-event';
import { FolderListDeletedEvent } from '@hub4b/models/events/folder-list-deleted-event';
import { FolderListUpdatedEvent } from '@hub4b/models/events/folder-list-updated-event';
import { FolderTabInfos } from '@hub4b/models/folder-tab-infos';
import { IAllSelectable } from '@hub4b/models/interfaces/all-selectable.interface';
import { IFilterable } from '@hub4b/models/interfaces/filterable.interface';
import { ListItem } from '@hub4b/models/list-item';
import { PredefinedFilter } from '@hub4b/models/predefined-filter';
import { SelectionContext } from '@hub4b/models/selection-context';
import { ColumnDefinition } from '@hub4b/models/server/column-definition';
import { FilterInfos } from '@hub4b/models/server/core/filter-infos';
import { SortInfos } from '@hub4b/models/server/core/sort-infos';
import { LockedType } from '@hub4b/models/server/enums/locked-type';
import { Rights } from '@hub4b/models/server/enums/rights';
import { Folder } from '@hub4b/models/server/folder';
import { StandByDatas } from '@hub4b/models/server/stand-by-datas';
import { View } from '@hub4b/models/server/view';
import { DialogService } from '@hub4b/services/actions/dialog.service';
import { FolderActionsService } from '@hub4b/services/actions/folder-actions.service';
import { NotificationService } from '@hub4b/services/actions/notification.service';
import { SpinnerService } from '@hub4b/services/core/spinner.service';
import { EventsManagerService } from '@hub4b/services/events/events-manager.service';
import { FolderCounterEventsService } from '@hub4b/services/events/folder-counter-events.service';
import { FollowedFoldersEventsService } from '@hub4b/services/events/followed-folders-events.service';
import { CustomersService } from '@hub4b/services/http/customers.service';
import { FolderDocumentsService } from '@hub4b/services/http/folder-documents.service';
import { FolderStatesService } from '@hub4b/services/http/folder-states.service';
import { FoldersService } from '@hub4b/services/http/folders.service';
import { ConnectedUsersStateService } from '@hub4b/services/state/connected-users-state.service';
import { FolderSearchStateService } from '@hub4b/services/state/folder-search-state.service';
import { ViewsStateService } from '@hub4b/services/state/views-state.service';
import { NgxSpinnerModule } from 'ngx-spinner';
import { forkJoin, Subject, takeUntil } from 'rxjs';
import { FollowedFoldersSearchBarComponent } from './followed-folders-search-bar/followed-folders-search-bar.component';

@Component({
    selector: 'app-followed-folders-datas-list',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MatTableModule,
        TableHeaderCellComponent,
        TableCellComponent,
        TableActionCellComponent,
        MatTableModule,
        MatSortModule,
        FoldersListCounterComponent,
        FaIconComponent,
        FollowedFoldersSearchBarComponent,
        NgxSpinnerModule,
        MatButtonModule,
    ],
    templateUrl: './followed-folders-datas-list.component.html',
    styleUrl: './followed-folders-datas-list.component.scss',
    providers: [FolderSearchStateService, FolderCounterEventsService]
})
export class FollowedFoldersDatasListComponent extends BaseDirective implements OnInit, IAllSelectable, IFilterable {
    private foldersService = inject(FoldersService);
    private notificationService = inject(NotificationService);
    private folderActionsService = inject(FolderActionsService);
    private dialogService = inject(DialogService);
    private connectedUserStateService = inject(ConnectedUsersStateService);
    private folderCounterEventsService = inject(FolderCounterEventsService);
    private folderStatesService = inject(FolderStatesService);
    private viewsStateService = inject(ViewsStateService);
    private eventsManagerService = inject(EventsManagerService);
    private cdRef = inject(ChangeDetectorRef);
    private followedFoldersEventsService = inject(FollowedFoldersEventsService);
    private folderDocumentsService = inject(FolderDocumentsService);
    private spinner = inject(SpinnerService);
    private folderSearchStateService = inject(FolderSearchStateService);
    private customersService = inject(CustomersService);

    public faRotateRight = faRotateRight;

    @HostListener('document:contextmenu', ['$event'])
    onDocumentContextMenu(event: MouseEvent) {
        // Désactive l'utilisation du clic-droit sur le navigateur
        event.preventDefault();
    }

    /** Emis lorsque l'utilisateur veut afficher le détail d'un dossier. */
    @Output()
    folderDisplayed: EventEmitter<FolderTabInfos> = new EventEmitter();

    /* Nombre de dossiers chargés à l'écran */
    public loadedFoldersCount: number = -1;

    public tableDataSource: MatTableDataSource<any> = new MatTableDataSource();

    paginator: MatPaginator = new MatPaginator(new MatPaginatorIntl(), this.cdRef);

    /** Contexte relatif à la selection des dossiers. */
    public selectionContext: SelectionContext;

    /** Liste des dossiers chargés */
    public folders: Folder[] = null;

    /* Nombre total de dossier correspondant aux filtres courants */
    public totalFoldersCount: number = -1;

    loadedRecordsCount: number = 50;

    /** Valeur du filtre courant. */
    public currentSort: Sort = null;

    /** Définitions des colonnes */
    displayedColumnsDefinitions: ColumnDefinition[] = [];

    /** Definition de la colonne 'Action'. */
    actionColumnDefinition: ColumnDefinition;

    /** Liste des noms des colonnes affichées dans le tableau de dossiers. */
    displayedColumnCodes: string[];

    /** Référence au tableau de dossiers. */
    @ViewChild('resultsTable')
    resultsTable: MatTable<Folder>;

    @ViewChild('resultsTable', { read: ElementRef })
    resultsTableElementRef: ElementRef;

    /* Reference au conteneur scrollable */
    @ViewChild('resultsContainer', { read: ElementRef })
    resultsContainerElementRef: ElementRef;

    private scrollingInProgress: boolean = false;

    protected lastScrollTop: number = 0;

    protected currentScrollTop: number = 0;

    protected currentScrollLeft: number = 0;

    /** Contexte relatif aux filtres de dossiers. */
    filterInfos: FilterInfos[] = null;

    generalFilterInfos: FilterInfos[] = null;

    /** Filtre prédéfini courant */
    private currentPredefinedFilter: PredefinedFilter = new PredefinedFilter();

    private filtersDefined$: Subject<number> = new Subject<number>();

    protected cancelPreviousRequest$: Subject<void> = new Subject<void>();

    private recordsPerPage = 50;

    public viewsMap: Map<number, View> = new Map<number, View>();

    constructor() {
        super();
        this.selectionContext = new SelectionContext(this.cdRef, this);

        this.displayedColumnsDefinitions.push(
            { propertyCode: 'systemIcons', size: 5, type: PropertyType.None, isStickyColumn: true, custom: false } as ColumnDefinition,
            { propertyCode: 'name', type: PropertyType.Text, header: 'Nom du dosssier', size: 20 } as ColumnDefinition,
            { propertyCode: 'viewName', type: PropertyType.Text, header: 'vue', size: 15 } as ColumnDefinition,
            { propertyCode: 'customerId', type: PropertyType.ListCustomers, header: 'Client', size: 20 } as ColumnDefinition,
            { propertyCode: 'folderStateId', type: PropertyType.List, header: 'Etat', size: 5 } as ColumnDefinition,
            { propertyCode: 'color', type: PropertyType.Color, header: 'Couleur', size: 10 } as ColumnDefinition,
            { propertyCode: 'creationDate', type: PropertyType.DateAndTime, displayFormat: 'dd/MM/yyyy', header: 'date de création', size: 10 } as ColumnDefinition,
            { propertyCode: 'updateDate', type: PropertyType.DateAndTime, displayFormat: 'dd/MM/yyyy', header: 'date de mise à jour', size: 10 } as ColumnDefinition
        );

        // Récupère la liste des noms de colonnes à afficher.
        this.displayedColumnCodes = this.displayedColumnsDefinitions.map((y) => y.propertyCode);

        // Récupère la définition de la colonne 'Action' et ajoute son nom dans la liste des noms de colonnes à afficher.
        this.actionColumnDefinition = this.getActionColumnDefinition();
        this.displayedColumnCodes.push(this.actionColumnDefinition.propertyCode);

        // Réinitialise le tri avec le tri par défaut paramétré pour la vue
        this.currentSort = {
            active: 'name',
            direction: SortDirections.ASC,
        };

        this.loadFolders();

        this.paginator.ngOnInit();
    }

    ngOnInit(): void {
        this.subscribeToFolderUpdateEvent();
        this.subscribeToFolderDeleteEvent();
        this.subscribeToFolderListRefreshEvent();
        this.subscribeToFilterContextChangeEvent();
        this.generalFilterInfos = [];
        this.filterInfos = [];
        this.paginator.pageSize = this.loadedRecordsCount;
        this.tableDataSource.paginator = this.paginator;
        this.cdRef.markForCheck();
    }

    /**
     * Charge les dossiers à afficher dans le tableau.
     */
    protected loadFolders() {
        this.cdRef.detach();
        this.loadedRecordsCount = this.recordsPerPage;
        this.paginator._changePageSize(this.loadedRecordsCount);

        this.cancelPreviousRequest$.next();

        const customerSortInfo: SortInfos = { columnName: CustomerPropertyCodes.NAME, direction: SortDirections.ASC, columnType: PropertyType.Text };

        forkJoin([
            this.folderStatesService.list().pipe(takeUntil(this.componentDestroyed$)),
            this.customersService.List(null, null, customerSortInfo).pipe(takeUntil(this.componentDestroyed$)),
            this.foldersService
                .getUserFollowedFolders(0, this.recordsPerPage, this.getSortInfos(), this.filterInfos, this.generalFilterInfos)
                .pipe(takeUntil(this.componentDestroyed$)),
        ]).subscribe({
            next: ([folderStatesList, customersList, followFoldersList]) => {
                let listItems: ListItem[] = [];

                folderStatesList.forEach((state) => {
                    listItems.push(new ListItem(state.id, state.name));
                });

                this.displayedColumnsDefinitions.find((x) => x.propertyCode === 'folderStateId').values = listItems;

                listItems = [];

                customersList.value.forEach((customer) => {
                    listItems.push(new ListItem(customer.id.toString(), customer.name));
                });

                this.displayedColumnsDefinitions.find((x) => x.propertyCode === 'customerId').values = listItems;

                let folders: Folder[] = [];

                this.viewsStateService.boxViews.forEach((view) => {
                    this.viewsMap.set(view.id, view);
                });

                this.viewsStateService.workflowViews.forEach((view) => {
                    this.viewsMap.set(view.id, view);
                });

                followFoldersList.value.forEach((f) => {
                    let folder = new Folder();
                    folder.copyFrom(f);
                    folder.expanded = false;
                    folder.viewName = this.viewsMap.get(folder.viewId).name;
                    folders.push(folder);
                });

                this.folders = folders;
                this.loadedFoldersCount = followFoldersList.value.length;

                this.totalFoldersCount = followFoldersList.totalCount;

                this.folderCounterEventsService.displayedFoldersCounterUpdated.next(new FolderCounterUpdatedEvent(this.loadedFoldersCount));

                this.folderCounterEventsService.totalFoldersCounterUpdated.next(new FolderCounterUpdatedEvent(followFoldersList.totalCount));

                this.tableDataSource.data = this.folders;
                this.tableDataSource.paginator = this.paginator;

                this.selectionContext.clearSelection();
                this.spinner.hide('follow-folders-spinner');
                this.cdRef.reattach();
                this.cdRef.markForCheck();
            },
            error: (e) => {
                console.log(e);
            },
        });
    }

    /**
     * Copie le texte sur lequel un clic-droit a été réalisé dans le presse-papier.
     * @param event : Événement clic-droit de la souris.
     */
    public copyTextIntoClipboard(event: MouseEvent): void {
        // Récupère le noeud HTML sur lequel l'utilisateur a fait un clic-droit.
        const node = event.target as Node;

        // Récupère le texte du noeud HTML
        const cellText = node.textContent;
        if (cellText) {
            navigator.clipboard
                .writeText(cellText)
                .then(() => this.notificationService.displayInfo('Presse-papier.', `Le texte \'${cellText}\' a été copié`))
                .catch((error) => this.notificationService.displayError('Presse-papier.', `Le texte n'a pas pu être été copié dans le presse-papier : \'${error}\'`));
        }
    }

    /**
     * Levé lorsque l'utilisateur ouvre le menu contextuel.
     * @param event : Événement d'ouverture du menu contextuel
     */
    public onContextualMenuOpen(event: ContextualMenuOpenEvent): void {
        if (this.selectionContext.selections.indexOf(event.index) === -1) {
            // Si la ligne pour laquelle l'utilisateur a affichée le menu contextuel n'est pas sélectionnée --> l'ajoute à la selection.
            this.selectionContext.handleSelection(event.sourceEvent, event.index);
        }
    }

    /**
     * Levé lorsque l'utilisateur selectionne une action du menu contextuel.
     * @param event : Événement de selection d'une action du menu contextuel.
     */
    public onContextualMenuSelect(event: ContextualMenuSelectEvent): void {
        switch (event.actionType) {
            case ContextualActionType.markAsRead:
                this.toggleReadState(true);
                break;
            case ContextualActionType.markAsUnread:
                this.toggleReadState(false);
                break;
            case ContextualActionType.addFile:
                this.addFile();
                break;
            case ContextualActionType.deleteFolder:
                this.deleteFolder();
                break;
            case ContextualActionType.displayFolder:
                this.displayFolder(true);
                break;
            case ContextualActionType.duplicate:
                this.duplicate();
                break;
            case ContextualActionType.setInStandByState:
                this.setInStandByState();
                break;
            case ContextualActionType.removeFromStandByState:
                this.removeFromStandByState();
                break;
            case ContextualActionType.CopyTrackingLink:
                this.CopyTrackingLink();
                break;
            case ContextualActionType.archive:
                this.Archive();
                break;
            case ContextualActionType.SetInWaitingArchivingState:
                this.SetInWaitingArchivingState();
                break;
            case ContextualActionType.RemoveFromWaitingArchivingState:
                this.removeFromWaitingArchivingState();
                break;
            case ContextualActionType.followFolder:
                this.followFolder();
                break;
            case ContextualActionType.stopFollowFolder:
                this.stopFollowFolder();
                break;
        }
    }

    /**
     * Basculer l'état "lu" du dossier en lu ou non lu.
     * @param readState Nouvel état de lecture du dossier : true pour lu, false pour non lu.
     */
    public toggleReadState(readState: boolean): void {
        this.selectionContext.selections
            .map((i) => this.folders[i])
            .forEach((folder) => {
                folder.expanded = false;
                this.folderActionsService.toggleReadStatus(folder, readState);
            });
    }

    /**
     * Ajoute un fichier à un dossier.
     */
    public addFile(): void {
        if (this.selectionContext.selections.length === 1) {
            const selectedFolder = this.folders[this.selectionContext.selections[0]];
            this.dialogService
                .openAddFile(selectedFolder.viewId, selectedFolder.id)
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe((document) => {
                    this.refreshDocuments(selectedFolder.viewId, selectedFolder.id, document.id);
                });
        } else {
            this.notificationService.displayWarning('Opération impossible', "L'ajout de fichier ne peut se faire que pour un dossier. Veuillez ne selectionner qu'un seul dossier");
        }
    }

    /**
     * Supprime les dossiers selectionnés.
     */
    public deleteFolder(): void {
        const selectedFolders = this.selectionContext.selections.map((i) => this.folders[i]);

        if (this.connectedUserStateService.getUserConnected().params.deletionConfirmationDisabled === true) {
            selectedFolders.forEach((folder) => {
                this.folderActionsService.deleteFolder(folder);

                // Met à jour les compteurs
                this.totalFoldersCount = this.totalFoldersCount - 1;
                this.loadedFoldersCount = this.loadedFoldersCount - 1;
                this.loadedRecordsCount = this.loadedRecordsCount - 1;

                this.folderCounterEventsService.displayedFoldersCounterUpdated.next(new FolderCounterUpdatedEvent(this.loadedFoldersCount));
                this.folderCounterEventsService.totalFoldersCounterUpdated.next(new FolderCounterUpdatedEvent(this.totalFoldersCount));
            });
        } else {
            this.dialogService
                .openDeleteFolder(selectedFolders)
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe((isConfirm) => {
                    if (isConfirm) {
                        selectedFolders.forEach((folder) => {
                            this.folderActionsService.deleteFolder(folder);

                            // Met à jour les compteurs
                            this.totalFoldersCount = this.totalFoldersCount - 1;
                            this.loadedFoldersCount = this.loadedFoldersCount - 1;
                            this.loadedRecordsCount = this.loadedRecordsCount - 1;

                            this.folderCounterEventsService.displayedFoldersCounterUpdated.next(new FolderCounterUpdatedEvent(this.loadedFoldersCount));
                            this.folderCounterEventsService.totalFoldersCounterUpdated.next(new FolderCounterUpdatedEvent(this.totalFoldersCount));
                        });
                    }
                });
        }
    }

    /**
     * Suit le dossier.
     */
    public followFolder(): void {
        const selectedFolders = this.selectionContext.selections.map((i) => this.folders[i]);

        selectedFolders.forEach((folder) => {
            this.folderActionsService.followFolder(folder);
        });
    }

    /**
     * Arrête le suivi du dossier.
     */
    public stopFollowFolder(): void {
        const selectedFolders = this.selectionContext.selections.map((i) => this.folders[i]);

        selectedFolders.forEach((folder) => {
            this.folderActionsService.stopFollowFolder(folder);
        });
    }

    /**
     * Duplique des dossiers selectionné
     */
    public duplicate(): void {
        const selectedFolders = this.selectionContext.selections.map((i) => this.folders[i]);

        this.dialogService
            .openDuplicate(selectedFolders)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((result) => {
                if (!!result) {
                    selectedFolders.forEach((folder) => {
                        this.folderActionsService.duplicateFolder(folder, result.duplicateCount);
                    });
                }
            });
    }

    /**
     * Place le dossier en stand-by.
     */
    public setInStandByState(): void {
        const selectedFolders = this.selectionContext.selections.map((i) => this.folders[i]);
        this.dialogService
            .openSetInStandByState(this.connectedUserStateService.getUserConnected().params.standByDuration)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((result) => {
                if (!!result) {
                    const data = new StandByDatas();
                    data.duration = result.duration;
                    data.endDate = result.endDate;

                    selectedFolders.forEach((folder) => {
                        this.folderActionsService.setInStandByState(folder, data);
                    });
                }
            });
    }

    /**
     * Retire le dossier du mode stand-by
     */
    public removeFromStandByState(): void {
        const selectedFolders = this.selectionContext.selections.map((i) => this.folders[i]);

        selectedFolders.forEach((folder) => {
            this.folderActionsService.removeFromStandByState(folder);
        });
    }

    public CopyTrackingLink(): void {
        if (this.selectionContext.selections.length === 1) {
            const selectedFolder = this.folders[this.selectionContext.selections[0]];

            this.foldersService.getTrackingLink(selectedFolder.id).subscribe((x) => {
                this.copyToClipboard(x);
            });
        } else {
            this.notificationService.displayWarning(
                'Opération impossible',
                "La copie du lien de suivi ne peut se faire que pour un dossier. Veuillez ne selectionner qu'un seul dossier"
            );
        }
    }

    /**
     * Archive le dossier.
     */
    public Archive(): void {
        this.selectionContext.selections
            .map((i) => this.folders[i])
            .forEach((folder) => {
                this.folderActionsService.archive(folder);
            });
    }

    /**
     * Place le dossier en attente d'archivage.
     */
    public SetInWaitingArchivingState(): void {
        this.selectionContext.selections
            .map((i) => this.folders[i])
            .forEach((folder) => {
                this.folderActionsService.SetInWaitingArchivingState(folder);
            });
    }

    /**
     * Retire le dossier de l'état en attente d'archivage.
     */
    public removeFromWaitingArchivingState(): void {
        const selectedFolders = this.selectionContext.selections.map((i) => this.folders[i]);

        selectedFolders.forEach((folder) => {
            this.folderActionsService.removeFromWaitingArchivingState(folder);
        });
    }

    copyToClipboard(value: string) {
        navigator.clipboard.writeText(value);
    }

    /** Événement lors du scroll du tableau */
    public onScroll(event) {
        const elem = event.target;
        this.currentScrollTop = elem.scrollTop;
        this.currentScrollLeft = elem.scrollLeft;
        if (
            this.scrollingInProgress === false &&
            Math.round(elem.offsetHeight + elem.scrollTop) >= elem.scrollHeight &&
            elem.scrollTop > this.lastScrollTop &&
            Math.round(elem.offsetHeight + this.lastScrollTop) < elem.scrollHeight &&
            this.loadedFoldersCount < this.totalFoldersCount
        ) {
            // Chargement de la "page suivante" si un chargement n'est pas déjà en cours & si le scrolling dépasse la hauteur de la barre de défilement
            // & si le scroll est vers le bas & si le precedent scroll n'avait pas déjà atteint la fin de la liste*
            // et enfin si le nombre de documents chargés est inférieur au nombre de documents total.
            this.scrollingInProgress = true;
            this.spinner.show('follow-folders-spinner');
            this.lastScrollTop = elem.scrollTop;
            this.foldersService
                .getUserFollowedFolders(this.loadedRecordsCount, this.recordsPerPage, this.getSortInfos(), this.filterInfos, this.generalFilterInfos)
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe((x) => {
                    x.value.forEach((item) => {
                        this.folders.push(item);
                    });
                    this.loadedFoldersCount = this.folders.length;
                    this.totalFoldersCount = x.totalCount;
                    this.folderCounterEventsService.displayedFoldersCounterUpdated.next(new FolderCounterUpdatedEvent(this.loadedFoldersCount));
                    this.folderCounterEventsService.totalFoldersCounterUpdated.next(new FolderCounterUpdatedEvent(x.totalCount));
                    this.tableDataSource.paginator = this.paginator;
                    this.tableDataSource.data = this.folders;
                    this.cdRef.detectChanges();
                    this.loadedRecordsCount += this.recordsPerPage;
                    this.paginator._changePageSize(this.loadedRecordsCount);
                    this.scrollingInProgress = false;
                    this.spinner.hide('follow-folders-spinner');
                });
        }
    }

    /**
     * Indique si l'élément specifié est selectionné.
     * @param index : index de l'élément à tester
     * @returns True si l'élément est selectionné, ou false autrement.
     */
    isSelected(index: number): boolean {
        return this.selectionContext.isSelected(index);
    }

    /**
     * Appelée lorsque l'utilisateur double-clique sur un élément de la liste.
     * @param event : Evenment clic de la souris.
     * @param index : Index de l'élément cliqué.
     */
    onMouseDoubleClick(event: MouseEvent, index: number) {
        this.selectionContext.clearSelection();
        this.selectionContext.selections = [index];
        // Garde une trace du dernier élément cliqué.
        this.selectionContext.lastSingleSelection = index;
        if (this.connectedUserStateService.hasRight(Rights.FolderEdit) && this.viewsStateService.getSelectedView().isArchive === false) {
            this.displayFolder(false);
        } else {
            // Si l'utilisateur n'a pas le droit d'edition des dossiers ou s'il s'agit d'une vue Arrchive --> affichage du dossier en consultation seule.
            this.displayFolder(true);
        }
    }

    /**
     * Appelée lorsque l'utilisateur clique sur un élément de la liste.
     * @param event : Evenment clic de la souris.
     * @param index : Index de l'élément cliqué.
     */
    onMouseClick(event: MouseEvent, index: number) {
        this.selectionContext.onMouseClick(event, index);
    }

    /**
     * Affiche le detail des dossiers selectionnés.
     */
    public displayFolder(readonly: boolean): void {
        this.selectionContext.selections
            .map((i) => this.folders[i])
            .forEach((folder) => {
                if (!readonly) {
                    this.foldersService
                        .lock(folder.viewId, folder.id)
                        .pipe(takeUntil(this.componentDestroyed$))
                        .subscribe({
                            next: (x) => {
                                this.folderDisplayed.emit(new FolderTabInfos(folder.id, folder.name, this.viewsMap.get(folder.viewId), readonly));

                                folder.lockedType = LockedType.LockedByCurrentUser;
                                this.eventsManagerService.emitListFolderUpdatedEvent(folder.viewId, folder.createCopy());
                                this.eventsManagerService.emitFollowedFoldersUpdatedEvent(folder.viewId, folder.createCopy());
                            },
                            error: (error: HttpErrorResponse) => {
                                this.notificationService.displayWarning('Action impossible', 'Le document est verrouillé par un autre utilisateur.');
                            },
                        });
                } else {
                    this.folderDisplayed.emit(new FolderTabInfos(folder.id, folder.name, this.viewsMap.get(folder.viewId), readonly));
                }
            });
    }

    /**
     * Retourne les Ids de tous les dossiers filtrés.
     * Implemntation de IAllSelectable.
     * @returns Un tableau d'Ids.
     */
    public getAllIds() {
        return this.folders.map((folder: Folder, index: number) => index);
    }

    /**
     * Applique les filtres de recherche et/ou de colonnes.
     */
    public applyFilters() {
        this.spinner.show('follow-folders-spinner');
        this.computeColumnsFilters();
        this.computeGeneralFilters();
        this.loadFolders();
    }

    public resetScroll() {
        this.currentScrollTop = 0;
        this.currentScrollLeft = 0;
        this.scrollingInProgress = false;
        this.lastScrollTop = 0;
        if (this.resultsContainerElementRef) {
            var nativ = this.resultsContainerElementRef.nativeElement;
            nativ.scrollTop = this.currentScrollTop;
            nativ.scrollLeft = this.currentScrollLeft;
        }
    }

    /**
     * Retourne la définition de la colonne 'Action'.
     * @returns: Une définition de colonne.
     */
    private getActionColumnDefinition(): ColumnDefinition {
        const def = new ColumnDefinition();
        def.propertyCode = 'action';
        def.header = '';
        def.size = 3;
        return def;
    }

    /**
     * S'abonne aux événements de mise à jour de dossier.
     */
    private subscribeToFolderUpdateEvent() {
        this.followedFoldersEventsService.foldersUpdated.pipe(takeUntil(this.componentDestroyed$)).subscribe((event: FolderListUpdatedEvent) => {
            if (!!event && !!this.folders) {
                let index = this.folders.findIndex((x) => x.id === event.folder.id);

                if (index !== -1) {
                    this.folders = this.folders.slice();
                    // Pour ne pas afficher la liste des documments et commentaires
                    event.folder.hasComment = false;
                    event.folder.hasDocument = false;
                    event.folder.viewName = this.folders.find((x) => x._id === event.folder.id).viewName; // Pour garder le nom de la vue
                    this.folders[index] = event.folder;

                    // this.fillExpandeedStatusInformation(this.folders, this.tableDataSource.data);
                    this.tableDataSource.data = this.folders;

                    this.folderCounterEventsService.displayedFoldersCounterUpdated.next(new FolderCounterUpdatedEvent(this.folders.length));

                    this.selectionContext.clearSelection();

                    this.cdRef.markForCheck();
                }
            }
        });
    }

    /**
     * S'abonne aux événements de Suppression de dossier.
     */
    private subscribeToFolderDeleteEvent() {
        this.followedFoldersEventsService.foldersDeleted.pipe(takeUntil(this.componentDestroyed$)).subscribe((event: FolderListDeletedEvent) => {
            if (!!event && !!this.folders) {
                let index = this.folders.findIndex((x) => x.id === event.folderId);

                if (index !== -1) {
                    // Le dossier est retiré de la liste
                    this.folders = ArrayHelper.RemoveElementFromArray(this.folders.slice(), event.folderId);
                    this.tableDataSource.data = this.folders;

                    this.cdRef.detectChanges();
                }
            }
        });
    }

    /**
     * S'abonne aux événements de rafraichissement de la liste des dossiers.
     */
    private subscribeToFolderListRefreshEvent() {
        this.followedFoldersEventsService.listNeedRefresh.pipe(takeUntil(this.componentDestroyed$)).subscribe((event) => {
            //console.warn(`Réception de l'événement FolderListRefreshEvent par le composant BaseDatasListComponent`, event);
            this.applyFilters();
            this.resetScroll();
            this.cdRef.markForCheck();
        });
    }

    /**
     * Trie les dossiers du tableau.
     * @param sort : information de tri.
     */
    public sortFolders(sort: Sort): void {
        this.currentSort = sort;

        this.applyFilters();
    }

    public onRefresh() {
        this.applyFilters();
        this.resetScroll();
        this.cdRef.markForCheck();
    }

    /**
     * Restaure la position de la barre de défilement (Utilisé lorsque l'on revient sur l'onglet de la liste après en avoir selectionné un autre). Sinon la barre de défilement est remise en haut.
     */
    public restoreScrollPosition() {
        setTimeout(() => {
            if (this.resultsContainerElementRef) {
                var nativ = this.resultsContainerElementRef.nativeElement;
                nativ.scrollTop = this.currentScrollTop;
                nativ.scrollLeft = this.currentScrollLeft;
            }
        }, 500);
    }

    /**
     * S'abonne aux événements de changement de contexte de filtrage.
     */
    private subscribeToFilterContextChangeEvent() {
        this.folderSearchStateService.onPredefinedFilterChange$.pipe(takeUntil(this.componentDestroyed$)).subscribe((x) => {
            if (x) {
                this.currentPredefinedFilter = x;
                // this.filtersDefined$.next(x.viewId);
                this.applyFilters();
                this.resetScroll();
                this.cdRef.markForCheck();
            }
        });
    }

    private refreshDocuments(viewId: number, folderId: string, selectedDocumentId: string = null) {
        this.eventsManagerService.emitHistorySectionUpdatedEvent(viewId, folderId);

        this.folderDocumentsService.list(viewId, folderId).subscribe((documents) => {
            this.eventsManagerService.emitDocumentsUpdatedEvent(folderId, documents, selectedDocumentId);
        });

        this.eventsManagerService.emitListFolderUpdatedEvent(viewId, folderId);
        this.eventsManagerService.emitFollowedFoldersUpdatedEvent(viewId, folderId);
    }

    /**
     * Génère le filtre de recherche
     */
    private computeGeneralFilters(): void {
        this.generalFilterInfos = [];

        if (!!this.currentPredefinedFilter.searchTerm) {
            if (!!this.displayedColumnsDefinitions) {
                this.displayedColumnsDefinitions
                    .filter((a) => a.propertyCode != 'viewName')
                    .forEach((item) => {
                        if (item.type == PropertyType.Text || item.type == PropertyType.Email || item.type == PropertyType.PhoneNumber) {
                            let filter: FilterInfos = {
                                columnName: item.propertyCode,
                                value: this.currentPredefinedFilter.searchTerm,
                                type: this.getColumnType(item.type),
                                filterType: this.getFilterType(item.type),
                                selectionMultiple: false,
                            };
                            this.generalFilterInfos.push(filter);
                        } else if (item.type == PropertyType.List || item.type == PropertyType.ListCustomers || item.type == PropertyType.ListUsers) {
                            let value = item.values.find((a) => a.text.toUpperCase() == this.currentPredefinedFilter.searchTerm.toUpperCase())?.text;
                            if (!!value) {
                                let filter: FilterInfos = {
                                    columnName: item.propertyCode,
                                    value: value,
                                    type: this.getColumnType(item.type),
                                    filterType: this.getFilterType(item.type),
                                    selectionMultiple: true,
                                };
                                this.generalFilterInfos.push(filter);
                            }
                        }
                    });
            }
        }
    }

    /**
     * Génére le filtre des colonnes.
     */
    private computeColumnsFilters(): void {
        this.filterInfos = [];

        if (this.currentPredefinedFilter.filteredAttributes) {
            this.currentPredefinedFilter.filteredAttributes.getKeys().forEach((attributeName: string) => {
                const filteredAttribute = this.currentPredefinedFilter.filteredAttributes.get(attributeName);

                let filter: FilterInfos = {
                    columnName: attributeName,
                    value: filteredAttribute.values.join('|'),
                    type: this.getColumnType(filteredAttribute.type),
                    filterType: filteredAttribute.values?.length > 1 ? FilterType.ListMultiple : this.getFilterType(filteredAttribute.type),
                    selectionMultiple: filteredAttribute.values?.length > 1,
                };

                this.filterInfos.push(filter);
            });
        }
    }

    private getFilterType(propertyType: PropertyType): FilterType {
        let filterType: FilterType = null;
        switch (propertyType) {
            case PropertyType.Date:
                filterType = FilterType.Date;
                break;
            case PropertyType.DateAndTime:
                filterType = FilterType.Date;
                break;
            case PropertyType.List:
            case PropertyType.ListCustomers:
            case PropertyType.ListUsers:
            case PropertyType.Color:
                filterType = FilterType.ListMultiple;
                break;
            case PropertyType.Long:
            case PropertyType.Decimal:
                filterType = FilterType.Numeric;
                break;
            default:
                filterType = FilterType.Text;
                break;
        }

        return filterType;
    }

    private getColumnType(propertyType: PropertyType): ColumnType {
        let columnType: ColumnType = null;
        switch (propertyType) {
            case PropertyType.Date:
                columnType = ColumnType.Date;
                break;
            case PropertyType.DateAndTime:
                columnType = ColumnType.Date;
                break;
            case PropertyType.List:
            case PropertyType.ListCustomers:
            case PropertyType.ListUsers:
            case PropertyType.Color:
                columnType = ColumnType.List;
                break;
            case PropertyType.Long:
            case PropertyType.Decimal:
                columnType = ColumnType.Numeric;
                break;
            default:
                columnType = ColumnType.Text;
                break;
        }

        return columnType;
    }

    /**
     * Retourne les informations sur le tri à envoyer au serveur.
     */
    private getSortInfos(): SortInfos {
        if (this.currentSort) {
            var columnDefinition = this.displayedColumnsDefinitions.find((x) => x.propertyCode === this.currentSort.active);

            if (columnDefinition) {
                return {
                    columnName: this.currentSort.active,
                    direction: this.currentSort.direction.toString(),
                    columnType: columnDefinition.type,
                };
            } else {
                // La colonne spécifiée ne correspond pas à un attribut de la vue (problème de configuration)
                return null;
            }
        } else {
            // Pas de tri --> recupération des données initiales
            return null;
        }
    }
}
